import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { usePWA } from "@/hooks/usePWA";
import { useTranslation } from "@/hooks/useTranslation";
import { hasUnsavedInput } from "@/lib/unsaved-input";
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Long enough that switching tabs for a moment never reloads the app.
export const HIDDEN_RELOAD_DELAY_MS = 30 * 60 * 1000;

/**
 * Component that manages PWA updates with smart behavior:
 * - Dev mode: Auto-reload immediately
 * - Production new visitors: Auto-reload silently
 * - Production active users: Show toast notification, then apply the update
 *   on their next route change, or once the tab has stayed hidden for
 *   `HIDDEN_RELOAD_DELAY_MS` with nothing typed in it, so nobody stays on an
 *   old build by ignoring the toast
 */
export const PWAUpdatePrompt = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isUpdateAvailable, applyUpdate } = usePWA();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const [isUserActive, setIsUserActive] = useState(false);
  const [pathnameAtToast, setPathnameAtToast] = useState<string | null>(null);
  const hasShownToast = useRef(false);
  const activityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track user activity - user is "active" after 10 seconds on the app
  useEffect(() => {
    activityTimerRef.current = setTimeout(() => {
      setIsUserActive(true);
      console.log("PWA: User marked as active");
    }, 10000); // 10 seconds

    return () => {
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, []);

  const handleUpdate = useCallback(async () => {
    try {
      console.log("PWA: Applying update...");
      await applyUpdate();
    } catch (error) {
      console.error("PWA: Failed to update:", error);
      window.location.reload();
    }
  }, [applyUpdate]);

  useEffect(() => {
    if (!isUpdateAvailable || hasShownToast.current) return;
    hasShownToast.current = true;

    const isDev = import.meta.env.DEV;

    // DEV MODE: Auto-reload immediately
    if (isDev) {
      console.log("PWA: Dev mode - auto-reloading...");
      handleUpdate();
      return;
    }

    // PRODUCTION - New visitor: Auto-reload silently
    if (!isUserActive) {
      console.log("PWA: New visitor - auto-reloading silently...");
      handleUpdate();
      return;
    }

    // PRODUCTION - Active user: Show toast
    console.log("PWA: Active user - showing update toast");
    setPathnameAtToast(pathname);
    toast({
      title: t("components.pwa.update.title"),
      description: t("components.pwa.update.description"),
      action: (
        <Button size="sm" onClick={handleUpdate} className="gap-2">
          <RefreshCw className="w-3 h-3" />
          {t("components.pwa.update.actions.restart")}
        </Button>
      ),
      duration: Infinity, // Toast stays until user acts
    });
  }, [isUpdateAvailable, isUserActive, pathname, handleUpdate, toast, t]);

  // Pending update: a route change the user made (a link, back, forward)
  // already replaces the screen, so reloading there costs nothing. A replace
  // is the app redirecting itself — `/chat` → `/chat/:id` right after the
  // first message is sent — and a reload there would cut that send short.
  useEffect(() => {
    if (pathnameAtToast === null || pathname === pathnameAtToast) return;
    if (navigationType === "REPLACE") return;
    handleUpdate();
  }, [pathname, pathnameAtToast, navigationType, handleUpdate]);

  // Pending update: a tab left hidden for a long while is not being used; it
  // comes back on the new build. Skipped when something is typed in it — the
  // next route change applies the update instead.
  useEffect(() => {
    if (pathnameAtToast === null) return;

    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    const clearHiddenTimer = () => {
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = null;
    };

    const handleVisibilityChange = () => {
      clearHiddenTimer();
      if (document.visibilityState !== "hidden") return;

      hiddenTimer = setTimeout(() => {
        if (document.visibilityState !== "hidden") return;
        if (hasUnsavedInput(document)) return;
        handleUpdate();
      }, HIDDEN_RELOAD_DELAY_MS);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearHiddenTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pathnameAtToast, handleUpdate]);

  // This component doesn't render anything, it only manages toasts
  return null;
};
